// src/app/growthbook.service.ts
import {Injectable, PLATFORM_ID, Inject} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {configureCache, type FeatureResult, GrowthBook} from '@growthbook/growthbook';
import {autoAttributesPlugin} from "@growthbook/growthbook/plugins";

@Injectable({providedIn: 'root'})
export class GrowthbookService {
    public static readonly FLAGS = {
        USE_ADVANCED_DASHBOARD: 'use-advanced-dashboard',
        SHOW_FUNNEL: 'show-funnel',
        SHOW_SPECIALIZED: 'show-specialized',
        SHOW_TOTAL_VISITS: 'show-total-visits-on-dashboard',
        SHOW_AI_FUNNEL_GENERATOR: 'show-ai-funnel-generator',
        SHOW_AI_QUERY_ASSISTANT: 'show-ai-query-assistant',
    }
    private readonly gb: GrowthBook;
    private hasNavigated = false;

    constructor(@Inject(PLATFORM_ID) private platformId: Object) {
        const isBrowser = isPlatformBrowser(this.platformId);

        this.gb = new GrowthBook({
            apiHost: "https://cdn.growthbook.io",
            clientKey: "sdk-7Hf3wsM3U3yakXvQ",
            enableDevMode: true,
            trackingCallback: (experiment, result) => {
                // This is where you would send an event to your analytics provider
                console.log("Viewed Experiment", {
                    experimentId: experiment.key,
                    variationId: result.key
                });
            },
            // Only use autoAttributesPlugin in browser environment
            plugins: isBrowser ? [autoAttributesPlugin()] : [],
            disableCrossOriginUrlRedirectExperiments: true,
            navigate: isBrowser ? (url: string) => this.safeNavigate(url) : undefined,
        });
    }


    /** Call once during app bootstrap to load flags */
    async init(timeoutMs = 2000) {
        await this.gb.init({timeout: timeoutMs}); // handles fetch + cache; safe to await to avoid flicker
        configureCache({
            // The localStorage key the cache will be stored under
            cacheKey: "gbFeaturesCache",
            // Consider features stale after this much time (60 seconds default)
            staleTTL: 1000 * 60,
            // Cached features older than this will be ignored (24 hours default)
            maxAge: 1000 * 60 * 60 * 24,
            // For Remote Eval only - limit the number of cache entries (~1 entry per user)
            maxEntries: 10,
            // When `false`, we add a `visibilitychange` listener to disable SSE when the page is idle
            disableIdleStreams: false,
            // Consider a page "idle" when it is hidden for this long (default 20 seconds)
            idleStreamInterval: 20000,
            // Set to `true` to completely disable both in-memory and persistent caching
            disableCache: true,
        })
    }

    /** Keep user attributes up to date (id, plan, etc.) */
    setAttributes(attrs: Record<string, unknown>) {
        this.gb.setAttributes(attrs);
    }

    /** SPA routing: tell GB the current URL (needed for Visual Editor/redirect tests) */
    setUrl(href: string) {
        this.gb.setURL(href);
    }

    /** Read flags */
    isOn(key: string) {
        return this.gb.isOn(key);
    }

    get<T = unknown>(key: string, fallback?: T): T {
        const r: FeatureResult<T | null> = this.gb.evalFeature(key);
        return (r.value ?? fallback) as T;
    }

    /** Optional: live updates from GrowthBook (SSE) */
    enableStreaming() {
        this.gb.init({streaming: true});
    }

    /** Optional: manually refresh flags (e.g., after login) */
    refresh() {
        return this.gb.refreshFeatures();
    }

    private safeNavigate(targetUrl: string | null | undefined): void {
        if (typeof window === 'undefined' || !targetUrl) {
            return;
        }

        // Prevent multiple navigations during the same session
        if (this.hasNavigated) {
            console.warn('[GrowthBook] Already navigated once, skipping additional redirect to:', targetUrl);
            return;
        }

        const normalizedTarget = this.normalizeUrl(targetUrl, window.location.href);
        const normalizedCurrent = this.normalizeUrl(window.location.href);

        if (!normalizedTarget || normalizedTarget === normalizedCurrent) {
            console.warn('[GrowthBook] Skipping redirect to current URL', targetUrl);
            return;
        }

        // Additional check: compare the actual URLs directly
        try {
            const currentUrl = new URL(window.location.href);
            const targetUrlObj = new URL(targetUrl, window.location.href);

            if (currentUrl.origin === targetUrlObj.origin &&
                currentUrl.pathname === targetUrlObj.pathname &&
                currentUrl.search === targetUrlObj.search) {
                console.warn('[GrowthBook] Skipping redirect - URLs are functionally identical', targetUrl);
                return;
            }
        } catch (e) {
            console.error('[GrowthBook] Error comparing URLs', e);
            return;
        }

        console.log('[GrowthBook] Navigating to:', targetUrl);
        this.hasNavigated = true;
        window.location.replace(targetUrl);
    }

    private normalizeUrl(url: string, base?: string): string {
        try {
            const parsed = base ? new URL(url, base) : new URL(url);
            parsed.hash = '';

            if (parsed.pathname !== '/' && parsed.pathname.endsWith('/')) {
                parsed.pathname = parsed.pathname.replace(/\/+$/, '');
                if (!parsed.pathname) {
                    parsed.pathname = '/';
                }
            }

            return `${parsed.origin}${parsed.pathname}${parsed.search}`;
        } catch {
            return '';
        }
    }
}
